import { Target, Hammer, BarChart3, TrendingUp, CheckCircle, Users, Leaf, MapPin, TreePine } from 'lucide-react';
import BlogArticleLayout from '../components/BlogArticleLayout';

export default function BlogNachhaltigkeit({ darkMode = false }) {
  const article = {
    title: "DerrDigital Green Initiative: 10% für eine saubere Zukunft",
    excerpt: "Wie wir mit regionalen Handwerksbetrieben und intelligenten Mülleimern unsere Umwelt revolutionieren werden.",
    author: "DerrDigital Team",
    authorBio: "Digitale Innovatoren mit Fokus auf nachhaltige Lösungen und lokale Gemeinschaftsförderung.",
    date: "2025-06-19",
    readTime: "8 min",
    category: "Nachhaltigkeit & Innovation",
    tags: ["Nachhaltigkeit", "Regional", "Handwerk", "Umwelt", "Innovation", "Community"],
    image: "from-green-400 to-blue-500",
    views: 1247,
    likes: 89,
    comments: 23,
    tableOfContents: [
      { id: "vision", title: "Unsere Vision: 10% für die Umwelt" },
      { id: "regional-partnership", title: "Regionale Partnerschaften stärken" },
      { id: "smart-system", title: "Intelligente Müllentsorgung" },
      { id: "current-status", title: "Aktueller Stand & Transparenz" },
      { id: "future-metrics", title: "Zukünftige Erfolgsmessung" },
      { id: "community-impact", title: "Gesellschaftlicher Einfluss" },
      { id: "roadmap", title: "Unser Fahrplan in die Zukunft" }
    ]
  };

  return (
    <BlogArticleLayout darkMode={darkMode} article={article}>

      <section id="vision">
        <h2>
          <Target size={20} style={{display:'inline',verticalAlign:'middle',marginRight:8}} />
          Unsere Vision: 10% für eine saubere Zukunft
        </h2>
        <p>
          Bei DerrDigital haben wir eine klare Mission: Wir werden 10% unseres gesamten Umsatzes in ein innovatives Müllentsorgungssystem investieren, das nicht nur unsere Umwelt schützt, sondern auch lokale Handwerksbetriebe stärkt und Gemeinden zusammenbringt.
        </p>
        <p>
          Diese Initiative wird weit mehr als nur ein Umweltprojekt sein – sie wird zum Herzstück unserer Unternehmensphilosophie werden und zeigen, dass digitale Innovation und nachhaltiges Handeln Hand in Hand gehen können.
        </p>

        <div className="callout">
          <h3 className="font-bold mb-2 text-green-600">🌱 Unser Versprechen:</h3>
          <ul>
            <li><strong>10% Umsatzinvestition:</strong> Kontinuierliche Finanzierung unseres Green-Projekts</li>
            <li><strong>Regionale Fertigung:</strong> Unterstützung lokaler Holzhandwerksbetriebe</li>
            <li><strong>Intelligente Technik:</strong> Füllstandsbasierte, effiziente Müllentsorgung</li>
            <li><strong>Vollständige Transparenz:</strong> Offene Kommunikation aller Zahlen und Fortschritte</li>
          </ul>
        </div>
      </section>

      <section id="regional-partnership">
        <h2>
          <Hammer size={20} style={{display:'inline',verticalAlign:'middle',marginRight:8}} />
          Regionale Partnerschaften stärken
        </h2>
        <p>
          Unsere Mülleimer werden nicht in anonymen Fabriken produziert, sondern von regionalen Handwerksbetrieben in liebevoller Holzfertigung hergestellt. Diese Entscheidung wird mehrere positive Auswirkungen haben:
        </p>

        <div className="grid-2">
          <div className="highlight-box">
            <h4 className="font-bold mb-3 text-amber-600 flex items-center">
              <TreePine size={20} style={{display:'inline',verticalAlign:'middle',marginRight:8}} />
              Nachhaltigkeit durch Holz
            </h4>
            <ul>
              <li>• Verwendung von nachhaltig gewonnenem Holz aus der Region</li>
              <li>• Langlebige und reparierbare Konstruktion</li>
              <li>• Natürliche Materialien statt Kunststoff</li>
              <li>• Kompostierbare Komponenten am Lebensende</li>
              <li>• Minimale CO2-Bilanz durch kurze Transportwege</li>
            </ul>
          </div>

          <div className="highlight-box">
            <h4 className="font-bold mb-3 text-blue-600 flex items-center">
              <Users size={20} style={{display:'inline',verticalAlign:'middle',marginRight:8}} />
              Lokale Wirtschaftsförderung
            </h4>
            <ul>
              <li>• Direkte Aufträge für regionale Schreinereien</li>
              <li>• Sicherung und Schaffung von Arbeitsplätzen vor Ort</li>
              <li>• Stärkung des traditionellen Handwerks</li>
              <li>• Förderung regionaler Wirtschaftskreisläufe</li>
              <li>• Persönliche Partnerschaften mit lokalen Betrieben</li>
            </ul>
          </div>
        </div>

        <div className="callout">
          <h3 className="font-bold mb-2 text-purple-600">🤝 Partnerschaft auf Augenhöhe:</h3>
          <p>
            Wir werden langfristige Partnerschaften mit ausgewählten Handwerksbetrieben eingehen, die nicht nur auf einmaligen Aufträgen basieren, sondern auf gemeinsamen Werten und nachhaltiger Zusammenarbeit. Unsere Partner werden Teil der DerrDigital-Familie und profitieren von planbaren, regelmäßigen Aufträgen.
          </p>
        </div>
      </section>

      <section id="smart-system">
        <h2>
          <BarChart3 size={20} style={{display:'inline',verticalAlign:'middle',marginRight:8}} />
          Intelligente Müllentsorgung der Zukunft
        </h2>
        <p>
          Unsere Mülleimer werden nicht nur schön anzusehen sein – sie werden auch intelligent funktionieren. Durch modernste Sensortechnik werden sie automatisch ihren Füllstand überwachen und nur dann geleert werden, wenn es wirklich nötig ist.
        </p>

        <div className="highlight-box">
          <h4 className="font-bold mb-2 text-blue-600">📊 Intelligente Features</h4>
          <ul>
            <li>• <strong>Füllstandssensoren:</strong> Präzise Messung des Müllvolumens</li>
            <li>• <strong>IoT-Vernetzung:</strong> Echtzeitdaten über Mobilfunk oder LoRaWAN</li>
            <li>• <strong>Route-Optimierung:</strong> Effiziente Entleerungsrouten durch KI</li>
            <li>• <strong>Predictive Analytics:</strong> Vorhersage von Füllständen basierend auf Mustern</li>
            <li>• <strong>Wartungsalerts:</strong> Automatische Benachrichtigung bei Reparaturbedarf</li>
          </ul>
        </div>

        <div className="highlight-box">
          <h4 className="font-bold mb-2 text-green-600">🌍 Umwelt-Benefits</h4>
          <ul>
            <li>• <strong>Reduzierte Fahrten:</strong> Bis zu 40% weniger Leerfahrten</li>
            <li>• <strong>CO2-Einsparung:</strong> Minimaler ökologischer Fußabdruck</li>
            <li>• <strong>Überlaufprävention:</strong> Keine überfüllten Mülleimer mehr</li>
            <li>• <strong>Datenbasierte Optimierung:</strong> Kontinuierliche Verbesserung der Effizienz</li>
            <li>• <strong>Saubere Umgebung:</strong> Weniger Müll in der Natur</li>
          </ul>
        </div>
      </section>

      <section id="current-status">
        <h2>
          <CheckCircle size={20} style={{display:'inline',verticalAlign:'middle',marginRight:8}} />
          Aktueller Stand & vollständige Transparenz
        </h2>
        <p>
          Wir bei DerrDigital glauben an absolute Transparenz. Deshalb möchten wir ehrlich mit Ihnen sein: Zum jetzigen Zeitpunkt haben wir noch keine Verkäufe erzielt und befinden uns noch in der Aufbauphase unseres Unternehmens.
        </p>

        <div className="callout">
          <h3 className="font-bold mb-2 text-orange-600">🏗️ Aktuelle Situation:</h3>
          <ul>
            <li><strong>Umsatz:</strong> Noch bei 0€ - wir stehen am Anfang unserer Reise</li>
            <li><strong>Investition:</strong> Daher noch keine konkreten Investitionssummen</li>
            <li><strong>Mülleimer:</strong> Noch keine aufgestellt, da Projekt noch nicht gestartet</li>
            <li><strong>Status:</strong> Intensive Planungs- und Vorbereitungsphase</li>
          </ul>
        </div>

        <p>
          Diese Ehrlichkeit ist uns wichtig, weil wir von Anfang an Vertrauen aufbauen möchten. Sobald unser Unternehmen erste Umsätze generiert, werden wir sofort mit der Umsetzung unseres 10%-Plans beginnen.
        </p>

        <div className="grid-3">
          <div className="highlight-box" style={{textAlign:'center'}}>
            <div className="text-3xl mb-2">💰</div>
            <h4 className="font-bold mb-2">Investitionssumme</h4>
            <p className="text-2xl font-bold text-orange-600">0€</p>
            <p>Wartet auf erste Umsätze</p>
          </div>
          <div className="highlight-box" style={{textAlign:'center'}}>
            <div className="text-3xl mb-2">🗂️</div>
            <h4 className="font-bold mb-2">Aufgestellte Mülleimer</h4>
            <p className="text-2xl font-bold text-orange-600">0</p>
            <p>Projekt startet bei ersten Verkäufen</p>
          </div>
          <div className="highlight-box" style={{textAlign:'center'}}>
            <div className="text-3xl mb-2">♻️</div>
            <h4 className="font-bold mb-2">Entsorgter Müll</h4>
            <p className="text-2xl font-bold text-orange-600">0kg</p>
            <p>Folgt sobald System läuft</p>
          </div>
        </div>
      </section>

      <section id="future-metrics">
        <h2>
          <TrendingUp size={20} style={{display:'inline',verticalAlign:'middle',marginRight:8}} />
          Zukünftige Erfolgsmessung & Kennzahlen
        </h2>
        <p>
          Sobald unser Projekt startet, werden wir detaillierte Kennzahlen erfassen und regelmäßig transparent kommunizieren. Hier erfahren Sie, welche Metriken wir verfolgen werden:
        </p>

        <div className="callout">
          <h3 className="text-xl font-bold mb-4 text-purple-600">📈 Finanzielle Kennzahlen</h3>
          <div className="grid-2">
            <div>
              <h4 className="font-semibold mb-2">Monatliche Berichte:</h4>
              <ul>
                <li>• Gesamtumsatz DerrDigital</li>
                <li>• 10%-Investitionssumme (exakt berechnet)</li>
                <li>• Verwendung der Mittel (detailliert)</li>
                <li>• Partnerbetrieb-Auszahlungen</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Jährliche Übersicht:</h4>
              <ul>
                <li>• Gesamtinvestition ins Projekt</li>
                <li>• ROI-Berechnung (Umwelt + Gesellschaft)</li>
                <li>• Kostenverteilung nach Kategorien</li>
                <li>• Prognose für Folgejahr</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="callout">
          <h3 className="text-xl font-bold mb-4 text-green-600">🗑️ Operative Metriken</h3>
          <div className="grid-2">
            <div>
              <h4 className="font-semibold mb-2">Mülleimer-Statistiken:</h4>
              <ul>
                <li>• Anzahl aufgestellter Mülleimer</li>
                <li>• Standorte und Abdeckung</li>
                <li>• Durchschnittliche Füllzeiten</li>
                <li>• Leerungsfrequenz pro Standort</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Umwelt-Impact:</h4>
              <ul>
                <li>• Gesamtmenge entsorgter Müll (kg)</li>
                <li>• Vermiedene Überläufe</li>
                <li>• CO2-Einsparung durch Routenoptimierung</li>
                <li>• Verbesserung der Umgebungssauberkeit</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="callout">
          <h3 className="text-xl font-bold mb-4 text-blue-600">🤝 Partnerschaft-Kennzahlen</h3>
          <div className="grid-2">
            <div>
              <h4 className="font-semibold mb-2">Lokale Wirtschaft:</h4>
              <ul>
                <li>• Anzahl Partnerbetriebe</li>
                <li>• Auftragssummen an Handwerker</li>
                <li>• Geschaffene/gesicherte Arbeitsplätze</li>
                <li>• Regionale Wertschöpfung</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Technologie-Metriken:</h4>
              <ul>
                <li>• Sensor-Zuverlässigkeit (%)</li>
                <li>• Datenübertragungsqualität</li>
                <li>• Systeverfügbarkeit</li>
                <li>• Wartungsintervalle</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section id="community-impact">
        <h2>
          <Users size={20} style={{display:'inline',verticalAlign:'middle',marginRight:8}} />
          Gesellschaftlicher Einfluss & Community-Building
        </h2>
        <p>
          Unser Projekt wird über die reine Müllentsorgung hinausgehen und eine echte Gemeinschaftsinitiative werden. Wir werden nicht nur Mülleimer aufstellen, sondern Bewusstsein schaffen und Menschen zusammenbringen.
        </p>

        <div className="grid-2">
          <div className="highlight-box">
            <h4 className="font-bold mb-3 text-indigo-600 flex items-center">
              <Users size={20} style={{display:'inline',verticalAlign:'middle',marginRight:8}} />
              Community-Engagement
            </h4>
            <ul>
              <li>• Bürgerbeteiligung bei Standortauswahl</li>
              <li>• Regelmäßige Community-Updates</li>
              <li>• Lokale Umweltbildungsprogramme</li>
              <li>• Partnerschaften mit Schulen und Vereinen</li>
              <li>• Freiwillige "Mülleimer-Paten" Programme</li>
            </ul>
          </div>

          <div className="highlight-box">
            <h4 className="font-bold mb-3 text-teal-600 flex items-center">
              <Leaf size={20} style={{display:'inline',verticalAlign:'middle',marginRight:8}} />
              Bildung & Bewusstsein
            </h4>
            <ul>
              <li>• Monatliche Umwelt-Workshops</li>
              <li>• Informationsmaterial zu Nachhaltigkeit</li>
              <li>• Digitale Bildungsplattform</li>
              <li>• Kooperationen mit Umweltorganisationen</li>
              <li>• Aufklärung über Müllvermeidung</li>
            </ul>
          </div>
        </div>

        <div className="callout">
          <h3 className="font-bold mb-2 text-yellow-600">🏆 Langfristige Vision:</h3>
          <p className="mb-3">
            Wir träumen von einer Zukunft, in der unser Projekt als Vorbild für andere Unternehmen dient. Unsere intelligenten Mülleimer werden nicht nur Müll sammeln, sondern Geschichten erzählen – Geschichten von erfolgreichen Partnerschaften, sauberen Gemeinden und einem Unternehmen, das Verantwortung übernimmt.
          </p>
          <p>
            Jeder Mülleimer wird zu einem Symbol für nachhaltige Unternehmensführung und zeigen, dass Profit und Umweltschutz perfekt harmonieren können.
          </p>
        </div>
      </section>

      <section id="roadmap">
        <h2>
          <MapPin size={20} style={{display:'inline',verticalAlign:'middle',marginRight:8}} />
          Unser Fahrplan in die grüne Zukunft
        </h2>
        <p>
          Hier ist unser detaillierter Zeitplan für die Umsetzung unserer Vision. Jeder Schritt ist sorgfältig durchdacht und wird transparent dokumentiert.
        </p>

        <div className="callout">
          <h3 className="text-xl font-bold mb-3 text-red-600">🚀 Phase 1: Grundsteinlegung (Erste 6 Monate nach ersten Verkäufen)</h3>
          <ul>
            <li>• <strong>Partnerbetriebe identifizieren:</strong> Auswahl von 3-5 regionalen Schreinereien</li>
            <li>• <strong>Prototyp-Entwicklung:</strong> Erste intelligente Mülleimer-Prototypen</li>
            <li>• <strong>Standort-Analyse:</strong> Identifikation der ersten 10 Aufstellungsorte</li>
            <li>• <strong>Technologie-Integration:</strong> Sensoren, IoT-Anbindung und Software-Entwicklung</li>
            <li>• <strong>Erste Investition:</strong> 10% des erreichten Umsatzes sofort in Projektstart</li>
          </ul>
        </div>

        <div className="callout">
          <h3 className="text-xl font-bold mb-3 text-orange-600">🔧 Phase 2: Aufbau & Implementierung (Monate 6-12)</h3>
          <ul>
            <li>• <strong>Mülleimer-Produktion:</strong> Erste 25 Mülleimer in regionaler Fertigung</li>
            <li>• <strong>Standort-Aufbau:</strong> Installation und Inbetriebnahme an ausgewählten Orten</li>
            <li>• <strong>Monitoring-System:</strong> Vollständige Überwachung und Datenerfassung</li>
            <li>• <strong>Community-Launch:</strong> Öffentlichkeitsarbeit und Bürgerbeteiligung</li>
            <li>• <strong>Erste Daten:</strong> Beginn der Erfolgsmessung und Kennzahlenerfassung</li>
          </ul>
        </div>

        <div className="callout">
          <h3 className="text-xl font-bold mb-3 text-green-600">📈 Phase 3: Wachstum & Optimierung (Jahr 2)</h3>
          <ul>
            <li>• <strong>Skalierung:</strong> Ausbau auf 50-100 Mülleimer je nach Umsatzentwicklung</li>
            <li>• <strong>Datenanalyse:</strong> Optimierung basierend auf ersten Erkenntnissen</li>
            <li>• <strong>Partnernetzwerk:</strong> Ausbau der Handwerksbetrieb-Kooperationen</li>
            <li>• <strong>Technologie-Upgrade:</strong> Verbesserung der Sensortechnik und Software</li>
            <li>• <strong>Transparenz-Berichte:</strong> Regelmäßige öffentliche Fortschrittsberichte</li>
          </ul>
        </div>

        <div className="callout">
          <h3 className="text-xl font-bold mb-3 text-blue-600">🌟 Phase 4: Expansion & Vorbildfunktion (Jahr 3+)</h3>
          <ul>
            <li>• <strong>Regionale Expansion:</strong> Ausdehnung auf benachbarte Gemeinden</li>
            <li>• <strong>Wissenstransfer:</strong> Unterstützung anderer Unternehmen bei ähnlichen Projekten</li>
            <li>• <strong>Innovation:</strong> Weiterentwicklung zu Smart-City-Lösungen</li>
            <li>• <strong>Bildungsinitiative:</strong> Aufbau eines Umweltbildungszentrums</li>
            <li>• <strong>Nachhaltigkeit:</strong> Zertifizierung und Auszeichnungen anstreben</li>
          </ul>
        </div>

        <div className="callout">
          <h3 className="text-xl font-bold mb-3 text-purple-600">💡 Unser Erfolgsversprechen:</h3>
          <p className="mb-3">
            Wir verpflichten uns dazu, jeden Schritt dieses Fahrplans transparent zu dokumentieren und regelmäßig über unsere Fortschritte zu berichten. Sollten wir von unserem Plan abweichen müssen, werden wir dies offen kommunizieren und die Gründe dafür erläutern.
          </p>
          <p className="font-semibold">
            Ihr Vertrauen ist unser wertvollstes Gut – und das werden wir niemals enttäuschen.
          </p>
        </div>

        <div className="callout" style={{marginTop:'2rem'}}>
          <h3 className="text-2xl font-bold mb-4 text-green-600">🌱 Fazit: Gemeinsam für eine saubere Zukunft</h3>
          <p className="mb-4 text-lg">
            Unser 10%-Versprechen ist mehr als nur ein Umweltprojekt – es ist unsere DNA, unser Herzstück und unser Beitrag für eine bessere Welt. Auch wenn wir heute noch am Anfang stehen, ist unsere Vision klar: Wir werden zeigen, dass Unternehmen Verantwortung übernehmen können und dabei erfolgreich sein können.
          </p>
          <p className="mb-4">
            Jeder Euro, den wir verdienen werden, macht uns zu einem Teil der Lösung. Jeder Mülleimer, den wir aufstellen werden, wird zu einem Symbol für nachhaltige Unternehmensführung. Jede Partnerschaft mit regionalen Handwerksbetrieben wird zu einer Investition in unsere Gemeinschaft.
          </p>
          <p className="font-semibold text-lg">
            Das ist unser Versprechen. Das ist unser Weg. Das ist DerrDigital.
          </p>
        </div>
      </section>

    </BlogArticleLayout>
  );
}
